import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { api, ApiRequestError } from "../api/client";
import { OPERATOR, PROFESSIONS, PROFESSION_VALUES } from "../../shared/constants";

interface GuestPrefill {
  office_name: string | null;
  profession: string;
  profession_label: string;
}

// No.2 アカウント作成。メール+パスワードのみ(Google/メール認証なし)。
// ゲスト営業フロー(/g/:token → /register?guest=<token>&proposal=<id>)からの引継ぎに対応。
export function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const guestToken = searchParams.get("guest") ?? "";
  const selectedProposalId = searchParams.get("proposal") ?? "";
  const [guest, setGuest] = useState<GuestPrefill | null>(null);
  const professionParam = searchParams.get("profession") ?? "";
  const initialProfession = PROFESSION_VALUES.includes(professionParam)
    ? professionParam
    : "administrative_scrivener";
  const [form, setForm] = useState({
    email: searchParams.get("email") ?? "",
    password: "",
    profession: initialProfession,
    last_name: searchParams.get("last_name") ?? "",
    first_name: searchParams.get("first_name") ?? "",
    phone: searchParams.get("phone") ?? "",
    agree: false,
  });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // ゲスト引継ぎ時は事務所名・士業種別をプリフィルする。
  useEffect(() => {
    if (!guestToken) return;
    api
      .get<GuestPrefill>(`/api/guest/${guestToken}`)
      .then((res) => {
        setGuest(res);
        setForm((f) => ({ ...f, profession: f.profession || res.profession }));
      })
      .catch(() => setGuest(null));
  }, [guestToken]);

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await register({
        ...form,
        ...(guestToken ? { guest_token: guestToken } : {}),
        ...(guestToken && selectedProposalId
          ? { selected_proposal_id: selectedProposalId }
          : {}),
      });
      navigate("/payment");
    } catch (err) {
      setError(
        err instanceof ApiRequestError ? err.message : "登録に失敗しました",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-card card wide">
        <Link to="/" className="brand center">
          {OPERATOR.serviceName}
        </Link>
        <h1>アカウントを作成する</h1>
        <p className="muted center">
          登録後、初期構築費用5,000円の銀行振込をご案内します。入金確認後に制作フローへ進みます。
        </p>
        {guest && (
          <div className="alert alert-info">
            {guest.office_name ? `${guest.office_name} 様` : "ご提案"}のデザイン案からお申し込みです。
            {selectedProposalId
              ? "選択されたデザインを引き継いで制作を進めます。"
              : "アカウント作成後、デザインをお選びいただけます。"}
          </div>
        )}
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={onSubmit} className="form">
          <label>
            メールアドレス <span className="req">必須</span>
            <input
              type="email"
              placeholder="例: info@example-law.jp"
              value={form.email}
              onChange={(e) => set("email", e.target.value)}
              autoComplete="email"
              required
            />
          </label>
          <label>
            パスワード <span className="req">必須</span>
            <input
              type="password"
              placeholder="8文字以上"
              value={form.password}
              onChange={(e) => set("password", e.target.value)}
              autoComplete="new-password"
              minLength={8}
              required
            />
          </label>
          <label>
            事務所種別 <span className="req">必須</span>
            <select
              value={form.profession}
              onChange={(e) => set("profession", e.target.value)}
              required
            >
              <option value="">選択してください</option>
              {PROFESSIONS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </label>
          <div className="form-row">
            <label>
              姓 <span className="req">必須</span>
              <input
                type="text"
                placeholder="例: 山田"
                value={form.last_name}
                onChange={(e) => set("last_name", e.target.value)}
                required
              />
            </label>
            <label>
              名 <span className="req">必須</span>
              <input
                type="text"
                placeholder="例: 太郎"
                value={form.first_name}
                onChange={(e) => set("first_name", e.target.value)}
                required
              />
            </label>
          </div>
          <label>
            電話番号 <span className="req">必須</span>
            <input
              type="tel"
              placeholder="例: 03-1234-5678"
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              autoComplete="tel"
              required
            />
          </label>
          <label className="checkbox">
            <input
              type="checkbox"
              checked={form.agree}
              onChange={(e) => set("agree", e.target.checked)}
              required
            />
            <span>
              <Link to="/terms" target="_blank">
                利用規約
              </Link>{" "}
              と{" "}
              <Link to="/privacy" target="_blank">
                プライバシーポリシー
              </Link>{" "}
              に同意します
            </span>
          </label>
          <button
            type="submit"
            className="btn btn-primary btn-block"
            disabled={submitting}
          >
            {submitting ? "作成中..." : "アカウントを作成して振込案内へ"}
          </button>
        </form>
        <p className="auth-alt">
          既にアカウントをお持ちの方は <Link to="/login">ログイン</Link>
        </p>
        <p className="muted small center">
          {OPERATOR.companyName} が運営しています。ご入力情報は暗号化して管理します。
        </p>
      </div>
    </div>
  );
}
